/**
 * 本番環境エラー監視サービス統合
 * Sentry、LogRocket等の外部エラー監視サービスとの統合を提供
 */

import type { AppError } from './error-handling';
import { logger } from './logger';

export interface ErrorMonitoringConfig {
  dsn?: string;
  environment: string;
  release?: string;
  userId?: string;
  sessionId?: string;
  enablePerformanceMonitoring?: boolean;
}

export interface ErrorContext {
  userId?: string | undefined;
  sessionId?: string | undefined;
  url: string;
  userAgent: string;
  timestamp: string;
  breadcrumbs?: Array<{
    timestamp: string;
    message: string;
    category: string;
    level: 'info' | 'warning' | 'error';
  }>;
  tags?: Record<string, string>;
  extra?: Record<string, unknown>;
}

class ErrorMonitoringService {
  private config: ErrorMonitoringConfig;
  private isInitialized = false;
  private breadcrumbs: ErrorContext['breadcrumbs'] = [];

  constructor() {
    this.config = {
      environment: (typeof process !== 'undefined' ? process.env.NODE_ENV : 'development') || 'development',
      release: (typeof process !== 'undefined' ? process.env.REACT_APP_VERSION : 'unknown') || 'unknown',
      enablePerformanceMonitoring: (typeof process !== 'undefined' ? process.env.NODE_ENV : 'development') === 'production',
    };
  }

  /**
   * エラー監視サービスを初期化
   */
  public initialize(config: Partial<ErrorMonitoringConfig> = {}): void {
    this.config = { ...this.config, ...config };

    if (this.config.environment === 'production' && this.config.dsn) {
      this.initializeExternalService();
    }

    this.isInitialized = true;
    logger.info('Error monitoring service initialized', { 
      environment: this.config.environment,
      hasExternalService: !!this.config.dsn 
    });
  }

  /**
   * 外部エラー監視サービス（Sentry等）の初期化
   */
  private initializeExternalService(): void {
    // 実際のプロジェクトでは以下のようにSentryを初期化
    /*
    import * as Sentry from '@sentry/react';
    
    Sentry.init({
      dsn: this.config.dsn,
      environment: this.config.environment,
      release: this.config.release,
      integrations: [
        new Sentry.BrowserTracing(),
      ],
      tracesSampleRate: this.config.enablePerformanceMonitoring ? 0.1 : 0,
      beforeSend(event) {
        // 個人情報のフィルタリング
        if (event.request?.headers) {
          delete event.request.headers['Authorization'];
          delete event.request.headers['Cookie'];
        }
        return event;
      },
    });
    */

    logger.info('External error monitoring service initialized');
  }

  /**
   * パンくずリストを追加
   */
  public addBreadcrumb(
    message: string, 
    category: string = 'navigation', 
    level: 'info' | 'warning' | 'error' = 'info'
  ): void {
    const breadcrumb = {
      timestamp: new Date().toISOString(),
      message,
      category,
      level,
    };

    this.breadcrumbs?.push(breadcrumb);

    // 最新100件のみ保持
    if ((this.breadcrumbs?.length || 0) > 100) {
      this.breadcrumbs = this.breadcrumbs?.slice(-100) || [];
    }

    // 外部サービスにも送信
    if (this.isProductionWithExternalService()) {
      // Sentry.addBreadcrumb(breadcrumb);
    }
  }

  /**
   * エラーを外部監視サービスに送信
   */
  public captureError(
    error: AppError, 
    context: Partial<ErrorContext> = {}
  ): void {
    if (!this.isInitialized) {
      logger.warn('Error monitoring service not initialized');
      return;
    }

    const errorContext = this.buildErrorContext(context);
    
    // 開発環境では詳細ログのみ
    if (this.config.environment === 'development') {
      logger.error('Development Error Capture', {
        error: error.toJSON(),
        context: errorContext,
        stack: error.stack,
      });
      return;
    }

    // 本番環境では外部サービスに送信
    if (this.isProductionWithExternalService()) {
      this.sendToExternalService(error, errorContext);
    }

    // 内部ログも残す
    logger.error('Production Error Captured', {
      errorId: this.generateErrorId(),
      errorType: error.type,
      errorCode: error.errorCode,
      message: error.userMessage,
      context: errorContext,
    });
  }

  /**
   * パフォーマンス指標を記録
   */
  public capturePerformance(
    name: string, 
    duration: number, 
    tags: Record<string, string> = {}
  ): void {
    if (!this.config.enablePerformanceMonitoring) {
      return;
    }

    const performanceData = {
      name,
      duration,
      tags,
      timestamp: new Date().toISOString(),
    };

    logger.info('Performance metric captured', performanceData);

    if (this.isProductionWithExternalService()) {
      // Sentry.addBreadcrumb({
      //   message: `Performance: ${name} took ${duration}ms`,
      //   category: 'performance',
      //   level: duration > 1000 ? 'warning' : 'info',
      //   data: performanceData,
      // });
    }
  }

  /**
   * ユーザーコンテキストを設定
   */
  public setUserContext(userId: string, metadata: Record<string, string> = {}): void {
    this.config.userId = userId;
    
    // metadata を使用（将来的にSentryで使用予定）
    void metadata;

    if (this.isProductionWithExternalService()) {
      // Sentry.setUser({
      //   id: userId,
      //   ...metadata,
      // });
    }

    this.addBreadcrumb(`User context set: ${userId}`, 'user', 'info');
  }

  /**
   * セッションIDを設定
   */
  public setSessionId(sessionId: string): void {
    this.config.sessionId = sessionId;
    
    if (this.isProductionWithExternalService()) {
      // Sentry.setTag('sessionId', sessionId);
    }

    this.addBreadcrumb(`Session started: ${sessionId}`, 'session', 'info');
  }

  /**
   * エラーコンテキストを構築
   */
  private buildErrorContext(context: Partial<ErrorContext> = {}): ErrorContext {
    return {
      userId: context.userId || this.config.userId,
      sessionId: context.sessionId || this.config.sessionId,
      url: context.url || (typeof window !== 'undefined' ? window.location.href : 'unknown'),
      userAgent: context.userAgent || (typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown'),
      timestamp: context.timestamp || new Date().toISOString(),
      breadcrumbs: context.breadcrumbs || this.breadcrumbs?.slice(-20) || [], // 最新20件
      tags: {
        environment: this.config.environment,
        release: this.config.release || 'unknown',
        ...context.tags,
      },
      extra: context.extra || {},
    };
  }

  /**
   * 外部サービスにエラーを送信
   */
  private sendToExternalService(error: AppError, context: ErrorContext): void {
    try {
      // 実際のプロジェクトでは以下のようにSentryに送信
      /*
      Sentry.withScope((scope) => {
        scope.setTag('errorType', error.type);
        scope.setTag('errorCode', error.errorCode || 'unknown');
        scope.setContext('errorDetails', {
          userMessage: error.userMessage,
          technicalDetails: error.technicalDetails,
          timestamp: error.timestamp,
        });
        scope.setContext('applicationContext', context);
        
        Sentry.captureException(error);
      });
      */

      // フォールバック: 独自のエラー送信API
      if (typeof fetch !== 'undefined') {
        fetch('/api/errors', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            error: error.toJSON(),
            context,
          }),
        }).catch((fetchError) => {
          logger.error('Failed to send error to monitoring service', fetchError);
        });
      }
    } catch (monitoringError) {
      logger.error('Error in error monitoring service', monitoringError);
    }
  }

  /**
   * エラーIDを生成
   */
  private generateErrorId(): string {
    return `err_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * 本番環境で外部サービスが利用可能かチェック
   */
  private isProductionWithExternalService(): boolean {
    return this.config.environment === 'production' && !!this.config.dsn;
  }

  /**
   * サービスをリセット（テスト用）
   */
  public reset(): void {
    this.isInitialized = false;
    this.breadcrumbs = [];
    this.config = {
      environment: process.env.NODE_ENV || 'development',
      release: process.env.REACT_APP_VERSION || 'unknown',
      enablePerformanceMonitoring: process.env.NODE_ENV === 'production',
    };
  }
}

// シングルトンインスタンス
export const errorMonitoring = new ErrorMonitoringService();

/**
 * パフォーマンス測定用デコレータ
 */
export function measurePerformance<T extends (...args: unknown[]) => unknown>(
  name: string,
  tags: Record<string, string> = {}
) {
  return function (
    target: unknown,
    propertyKey: string,
    descriptor: TypedPropertyDescriptor<T>
  ): TypedPropertyDescriptor<T> {
    void target; // デコレータでは未使用
    const originalMethod = descriptor.value;

    descriptor.value = (async function (this: unknown, ...args: Parameters<T>) {
      const startTime = performance.now();
      
      try {
        const result = await originalMethod?.apply(this, args);
        const duration = performance.now() - startTime;
        
        errorMonitoring.capturePerformance(name, duration, {
          ...tags,
          method: propertyKey,
          status: 'success',
        });
        
        return result;
      } catch (error) {
        const duration = performance.now() - startTime;
        
        errorMonitoring.capturePerformance(name, duration, {
          ...tags,
          method: propertyKey,
          status: 'error',
        });
        
        throw error;
      }
    } as T);

    return descriptor;
  };
}

/**
 * React Error Boundary用のエラーレポート関数
 */
export const reportErrorBoundaryError = async (
  error: Error, 
  errorInfo: { componentStack: string | null }
): Promise<void> => {
  const { AppError, ErrorType } = await import('./error-handling');
  
  const appError = new AppError(
    ErrorType.GENERIC,
    'React component error occurred',
    `${error.message}\nComponent Stack: ${errorInfo.componentStack || 'Unknown'}`,
    'REACT_ERROR_BOUNDARY'
  );

  errorMonitoring.captureError(appError, {
    tags: { 
      errorBoundary: 'true',
      component: 'ErrorBoundary' 
    },
    extra: {
      componentStack: errorInfo.componentStack || 'Unknown',
      originalError: {
        name: error.name,
        message: error.message,
        stack: error.stack,
      },
    },
  });
};